import { type ReactNode, useEffect, useState } from 'react'
import {
  type SessionValidation,
  validatePersistedSession,
} from '~/entities/session/model/validate-persisted-session'
import * as m from '~/paraglide/messages'
import { storageManager } from '~/shared/lib/storage-manager'
import { supabase } from '~/shared/lib/supabase'

/**
 * Holds the app on first load until any persisted session is proven live, so no
 * route renders against a stale token (which otherwise rendered a blank page).
 * Logged-out users pass straight through, and a dead session is cleared here so
 * the app lands on /login. It does NOT route valid sessions anywhere - the routes
 * (login, /upgrade's own guard) own that, so recovery/invite links stay untouched.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionValidation | 'pending'>('pending')

  // The one place app-owned auth state is torn down. Any sign-out - user
  // logout, the post-password-set signOut, or an expiry-driven one - emits
  // SIGNED_OUT, so business/admin keys can never outlive the Supabase session.
  // Mounted for the app's lifetime, so no signOut path can slip past it.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') storageManager.clearSessionData()
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    let cancelled = false
    validatePersistedSession()
      .then((result) => {
        if (!cancelled) setStatus(result)
      })
      .catch(() => {
        // Never trap the app behind a validator failure.
        if (!cancelled) setStatus('ok')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (status === 'pending') {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="inline-block border-4 border-current border-r-transparent border-solid rounded-full w-8 h-8 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite] align-[-0.125em]" />
          <p className="mt-2 text-muted-foreground text-sm">{m.loading()}</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
